// File: src/pages/Analysis/Analysis.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingScreen from '../../components/Analysis/LoadingScreen';
import styles from './Analysis.module.css';

const Analysis = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [analysisData, setAnalysisData] = useState(null);
  const [error, setError] = useState(null);
  
  // Get analysis ID from URL params
  const analysisId = searchParams.get('id');
  const fileName = searchParams.get('file') || 'insurance-policy.pdf';
  
  // Analysis timing - can be customized based on file size or type
  const estimatedTime = 30000; // 30 seconds

  useEffect(() => {
    // Redirect if no analysis ID
    if (!analysisId) {
      navigate('/', { replace: true });
      return;
    }

    // Optional: Fetch analysis status from backend
    // This could check if the analysis is already complete
    checkAnalysisStatus();
  }, [analysisId, navigate]);

  const checkAnalysisStatus = async () => {
    try {
      // Replace with your actual API endpoint
      const response = await fetch(`/api/analysis/${analysisId}/status`);
      
      if (response.ok) {
        const data = await response.json();
        
        // If analysis is already complete, redirect to results
        if (data.status === 'complete') {
          navigate(`/results/${analysisId}`, { replace: true });
          return;
        }
        
        // If analysis failed, show error
        if (data.status === 'failed') {
          setError(data.error || 'Analysis failed. Please try again.');
          return;
        }
        
        // Set any available data
        setAnalysisData(data);
      }
    } catch (err) {
      console.error('Error checking analysis status:', err);
      // Continue with loading screen - don't block user experience
    }
  };

  const handleAnalysisComplete = async () => {
    try {
      // Optional: Verify analysis is actually complete
      const response = await fetch(`/api/analysis/${analysisId}/status`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'complete') {
          // Navigate to results with analysis data
          navigate(`/results/${analysisId}`, { 
            replace: true,
            state: { 
              analysisData: data.results,
              fromAnalysis: true 
            }
          });
          return;
        }
      }
      
      // Fallback: navigate to results even if API call fails
      navigate(`/results/${analysisId}`, { 
        replace: true,
        state: { fromAnalysis: true }
      });
      
    } catch (error) {
      console.error('Error completing analysis:', error);
      
      // Still navigate to results - don't block the user
      navigate(`/results/${analysisId}`, { 
        replace: true,
        state: { fromAnalysis: true }
      });
    }
  };

  const handleRetry = () => {
    // Clear error and restart analysis
    setError(null);
    setAnalysisData(null);
    
    // Optionally restart the analysis process
    checkAnalysisStatus();
  };

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  // Error state
  if (error) {
    return (
      <div className={styles.analysisPage}>
        <div className={styles.errorContainer}>
          <div className={styles.errorCard}>
            <div className={styles.errorIcon}>💥</div>
            <h1 className={styles.errorTitle}>Analysis Failed</h1>
            <p className={styles.errorMessage}>
              {error}
            </p>
            <div className={styles.errorActions}>
              <button 
                className={styles.retryButton}
                onClick={handleRetry}
              >
                Try Again
              </button>
              <button 
                className={styles.homeButton}
                onClick={handleGoHome}
              >
                Upload New Policy
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.analysisPage}>
      <LoadingScreen 
        fileName={fileName}
        onComplete={handleAnalysisComplete}
        estimatedTime={estimatedTime}
        className={styles.loadingScreen}
      />
      
      {/* Optional: Analysis metadata for debugging */}
      {process.env.NODE_ENV === 'development' && (
        <div className={styles.debugInfo}>
          <p>Analysis ID: {analysisId}</p>
          <p>File: {fileName}</p>
          <p>Status: {analysisData?.status || 'processing'}</p>
        </div>
      )}
    </div>
  );
};

export default Analysis;